// One notification row (bell drawer and centre): English title from the stored key, the amount only
// when the server included it (members with payments.read), a deep link, and mark-as-read.
import { formatMoney, formatRelative } from "@ady/format";
import { t } from "@ady/i18n";
import { cx } from "@ady/ui";
import { useShell } from "../shell/context";
import { AppLink } from "../shell/AppLink";
import type { AppNotification } from "./api";
import { resolveLink, titleOf } from "./live";
import { NTF_TID } from "./testids";

export function NotificationItem({
  n,
  onOpen,
  compact,
}: {
  n: AppNotification;
  onOpen: (n: AppNotification) => void;
  /** The bell's dropdown: one line, the title with the time on the right (the amount on hover). */
  compact?: boolean;
}) {
  const { membership } = useShell();
  const href = resolveLink(n.link, membership);
  const amount =
    typeof n.params.total_paisa === "number" ? formatMoney(n.params.total_paisa) : null;
  const body = compact ? (
    <span
      className="flex min-w-0 flex-1 items-center gap-2"
      title={amount ? `${titleOf(n)} · ${amount}` : titleOf(n)}
    >
      <span
        className={cx(
          "min-w-0 flex-1 truncate text-xs",
          n.read ? "text-fg-secondary" : "font-semibold text-fg",
        )}
      >
        {titleOf(n)}
      </span>
      <span className="flex-none text-2xs text-fg-tertiary">{formatRelative(n.created_at)}</span>
    </span>
  ) : (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className={cx("text-md", n.read ? "text-fg-secondary" : "font-semibold text-fg")}>
        {titleOf(n)}
      </span>
      {amount ? (
        <span className="text-sm text-fg-secondary">
          {t("notifications.centre.amount", { amount })}
        </span>
      ) : null}
      <span className="text-xs text-fg-tertiary">{formatRelative(n.created_at)}</span>
    </span>
  );
  return (
    <li
      data-testid={NTF_TID.centreItem(n.id)}
      data-read={n.read ? "true" : "false"}
      className={cx(
        "flex rounded-sm hover:bg-hover",
        compact ? "items-center gap-2 px-2 py-1" : "items-start gap-3 px-2 py-2",
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          compact ? "size-1.5 flex-none rounded-full" : "mt-2 size-2 flex-none rounded-full",
          n.read ? "bg-transparent" : compact ? "bg-link" : "bg-action-bg",
        )}
      />
      {href ? (
        <AppLink
          href={href}
          onClick={() => onOpen(n)}
          className="flex min-w-0 flex-1 text-fg no-underline"
        >
          {body}
        </AppLink>
      ) : (
        <button type="button" onClick={() => onOpen(n)} className="flex min-w-0 flex-1 text-start">
          {body}
        </button>
      )}
    </li>
  );
}
